import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

import {
	addRafThrottledScrollListener,
	getScrollSelectedHeadingId,
	resolveHeadings,
} from './scrollSelection.ts';
import type { TableOfContentsItem } from './tableOfContentsTypes.ts';

const VIEW_PAGE_SCROLL_CONTAINER_ID = 'AkMainContent';

export function useRendererScrollSelection(
	items: TableOfContentsItem[],
	isEnabled: boolean,
	triggerRef: RefObject<HTMLElement>,
): string | undefined {
	const [selectedHeadingId, setSelectedHeadingId] = useState<string>();

	useEffect(() => {
		if (!isEnabled || items.length === 0) {
			setSelectedHeadingId(undefined);
			return;
		}

		const ownerDocument = triggerRef.current?.ownerDocument;
		const ownerWindow = ownerDocument?.defaultView;
		if (!ownerDocument || !ownerWindow) {
			setSelectedHeadingId(undefined);
			return;
		}

		const resolvedHeadings = resolveHeadings(items, (headingId) =>
			ownerDocument.getElementById(headingId),
		);
		const scrollContainer = ownerDocument.getElementById(VIEW_PAGE_SCROLL_CONTAINER_ID);
		const scrollTarget = scrollContainer ?? ownerDocument;
		const updateSelectedHeading = () => {
			const nextSelectedHeadingId = getScrollSelectedHeadingId(
				resolvedHeadings,
				scrollContainer?.getBoundingClientRect().top ?? 0,
			);
			setSelectedHeadingId((currentSelectedHeadingId) =>
				currentSelectedHeadingId === nextSelectedHeadingId
					? currentSelectedHeadingId
					: nextSelectedHeadingId,
			);
		};

		updateSelectedHeading();
		return addRafThrottledScrollListener(scrollTarget, ownerWindow, updateSelectedHeading);
	}, [isEnabled, items, triggerRef]);

	return selectedHeadingId;
}
